import React from 'react';
import PropTypes from 'prop-types';

import { Trans, useTranslation } from 'react-i18next';
import { Icon, Modal, Button, WeezHeader, LinkWithActions, MenuContainer, Status, Form, Message, WelcomeModal, ToastContext, Loader } from '@weezevent/nacre';
import Minisite from '../../models/minisite';
import Feedback from '../../models/feedback';
import EditMenu from './editMenu';
import Block from '../../models/block';
import ConfirmModal from '../../components/confirmModal';
import BlockFormContainer from './blocks';
import { fetcher } from '@weezevent/weezjs-core';
import { Template, findBlockById, getBlock } from './editForm';
import EventModel from '../../models/eventModel';
import Styles from '../../styles/minisite/minisite.module.css';
import SchedulePublish from './timeInputs';
import moment from 'moment-timezone';
import useMixpanel from '../../context/useMixpanel';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { usePermissions } from '../../user/permissions';
import useAppContext from '../../context/useAppContext';
import retry from 'async-retry';

const BLOCK_MAPPING = {
    background_color: 'perso-css',
    primary_color: 'perso-css',
    light_primary_color: 'perso-css',
    reset_colors: 'perso-css',
    background_hide: 'perso-css',
    url: 'image-header',
    font_family: 'perso-css',
    favicon: 'favicon',
    social_img: 'seo',
    event_banner: 'event-image-header',
    event_social: 'event-social-opengraph',
    language_dropdown: 'language-dropdown'
};

const BLOCK_PLACEMENT = {
    'image-header': 'header',
    'event-image-header': 'header',
    'perso-css': 'perso-css',
    favicon: 'favicon',
    seo: 'opengraph',
    'date-and-location': 'column-left',
    'date-light': 'column-left',
    cta: 'cta-event',
    'language-dropdown': 'language-picker',
    'location-light': 'column-left',
    'event-contact': 'column-left',
    'event-description': 'column-left',
    'event-widget': 'column-left',
    'event-social-opengraph': 'opengraph'
};

const NAME_ATTR = {
    social_img: 'image',
    favicon: 'url'
};

const VIEWPORT_SIZE = {
    mobile: { width: '480px', height: '100vh' },
    tablet: { width: '720px', height: '100vh' },
    desktop: { width: '100%', height: '100vh' }
};

const isToday = value => {
    const FORMAT = 'YYYY-MM-DD';
    return moment(value).format(FORMAT) === moment().format(FORMAT);
};
const isBeforeMinute = value => {
    return moment(value).minute() <= moment().minute();
};
const isBeforeHour = value => {
    return moment(value).hour() <= moment().hour();
};

const getLanguage = data => {
    if (!data) {
        return null;
    }
    if (data.language && typeof data.language === 'string' && data.language.length === 2) {
        return `${data.language}-${data.language}`;
    }
    return data.language;
};

const createBlockData = (place, foundBlock, data = '') => ({
    id: place.block,
    place: place.name,
    position: place.position,
    type: foundBlock?.type,
    event: foundBlock?.event,
    data
});

export default function MinisiteEditContainer({ organization, event, standalone }) {
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const langParam = params.get('lang');

    const history = useHistory();

    const [{ reloadEvent: reloadEventContext }] = useAppContext();

    const siteId = event.site;

    const mixpanel = useMixpanel();
    const permissions = usePermissions();

    const [html, setHtml] = React.useState(null);
    const [previousBlocks, setPreviousBlocks] = React.useState([]);
    const [currentBlocks, setCurrentBlocks] = React.useState([]);
    const [selectedPlace, setSelectedPlace] = React.useState(null);
    const [currentPosition, setCurrentPosition] = React.useState(null);
    const [currentTemplate, setCurrentTemplate] = React.useState(null);
    const [editBlock, setEditBlock] = React.useState(null);
    const [giveFeedback, setGiveFeedback] = React.useState(false);
    const [feedbackForm, setFeedbackForm] = React.useState('');
    const [iframeSize, setIframeSize] = React.useState('desktop');
    const [schedule, setSchedule] = React.useState(null);
    const [scheduleModal, setScheduleModal] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [submitSuccess, setSubmitSuccess] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);

    const hasTranslationCapability = React.useMemo(() => {
        return permissions.hasCapacities(['GeminoCapacity.LANGUAGES'], organization.id);
    }, [permissions, organization]);

    const openPreview = React.useCallback(() => history.push(`${location.pathname}/preview`), [history, location]);

    const closePreview = React.useCallback(() => history.push(`${location.pathname.replace('/preview', '')}`), [history, location]);

    const [minisite, isLoadingMinisite, syncMinisite] = Minisite.useApiModel({
        cache: false,
        organizationId: organization.id,
        id: siteId,
        onSuccess: minisite => {
            if (minisite && minisite.delivery && minisite.delivery.on_date) {
                setSchedule(minisite.delivery.on_date);
            }
        }
    });

    const hexToRGBA = React.useCallback((color, opacity) => {
        if (opacity < 0) {
            throw new Error('Opacity cannot be lower that 0');
        }
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r},${g},${b},${opacity})`;
    }, []);

    //fix bug TypeError: func.apply is not a function with Immediately Invoked Function Expression
    React.useEffect(() => {
        const trackingMixpanel = () => mixpanel.track('VIEW - Minisite > Edit');
        trackingMixpanel();
    }, []);

    const [blocks, isLoadingBlocks, syncBlocks] = Block.useApiModel({
        cache: false,
        organizationId: organization.id,
        siteId: siteId
    });

    const blockCss = getBlock(blocks, 'perso-css');
    const blockCssPayload = blockCss ? blockCss.payload : {};

    const blockImagePayload = React.useMemo(() => {
        const blockImage = getBlock(blocks, 'image-header');
        return blockImage ? blockImage.payload : {};
    }, [minisite, blocks]);

    //find a tab with empty slug
    const tab = minisite && minisite.tabs && minisite.tabs.find(tab => !tab.slug);
    const tabIndex = minisite && minisite.tabs && minisite.tabs.findIndex(tab => !tab.slug);

    const [isOpen, setIsOpen] = React.useState(false);

    const onSelectPlace = React.useCallback(
        place => {
            setSelectedPlace(place);
            setIsOpen(true);
        },
        [setSelectedPlace, selectedPlace, setIsOpen]
    );

    const onSelectPosition = React.useCallback(
        (position, place) => {
            setCurrentPosition(position);
            setSelectedPlace(place);
            setIsOpen(true);
        },
        [setSelectedPlace, setCurrentPosition, selectedPlace, setIsOpen]
    );

    const [deleteBlock, setDeleteBlock] = React.useState(false);

    const onGiveFeedback = React.useCallback(() => {
        const feedback = new Feedback();

        feedback
            .generate({ slug: 'minisite-beta', text: feedbackForm })
            .then(response => {
                if (response) {
                    mixpanel.track('ACTION - Minisite > Feedback');
                    setFeedbackForm(null);
                    setGiveFeedback(false);
                    toast.success(t(`ticket.event.minisite.toast.feedbackSuccess`));
                }
            })
            .catch(e => {
                toast.error(t(`ticket.event.minisite.toast.feedbackError`));
            });
    }, [minisite, feedbackForm]);

    const onDeleteBlock = React.useCallback(() => {
        const newMinisite = new Minisite();
        newMinisite.id = minisite.get('id');
        const placements = [{ block: deleteBlock.id, name: deleteBlock.place, position: null }];
        newMinisite.tabs = [
            {
                id: minisite.tabs[tabIndex].id,
                placements
            }
        ];
        newMinisite
            .save({ organizationId: organization.id })
            .then(() => {
                toast.success(t('ticket.event.minisite.block.removeSuccess'));
                mixpanel.track('ACTION - Minisite > Delete Block');
                setDeleteBlock(false);
                syncBlocks();
                syncMinisite();
            })
            .catch(e => {
                console.error(e);
            });
    }, [deleteBlock, syncBlocks, syncMinisite, minisite]);

    const onEdit = React.useCallback(block => {
        setIsOpen(true);
        setSelectedPlace(block.place);
        setEditBlock(block);
    });

    const initialValues = React.useMemo(() => {
        const seo = getBlock(blocks, 'seo');
        const favicon = getBlock(blocks, 'favicon');
        const seo_event = getBlock(blocks, 'event-social-opengraph');
        const languageBlock = getBlock(blocks, 'language-dropdown');
        return {
            primary_color: blockCssPayload.primary_color || '#0032FA',
            light_primary_color: blockCssPayload.light_primary_color || '#F5F5F5',
            background_color: blockCssPayload.background_color || '#F5F5F5',
            background_hide: blockCssPayload.background_hide || false,
            url: blockImagePayload.url || '',
            font_family: blockCssPayload.font_family || 'Graphik',
            domain: minisite && minisite.domain,
            slug: minisite && minisite.slug,
            enable_seo_index: minisite && minisite.enable_seo_index,
            password_protection: (minisite && minisite.password_protection) || false,
            password: (minisite && minisite.password) || '',
            favicon: favicon ? favicon.payload.url : '',
            social_img: seo_event ? event.full_picture : seo ? seo.payload.image : '',
            cookies: '',
            language: getLanguage(minisite) || 'fr-fr',
            language_dropdown: languageBlock || null
        };
    }, [blockCssPayload, blockImagePayload, minisite, blocks]);

    const updateIframe = React.useCallback(() => {
        syncMinisite();
        syncBlocks();
    }, [syncMinisite, syncBlocks]);

    React.useEffect(() => {
        const blockCss = getBlock(blocks, 'perso-css');
        if (blocks && !blockCss) {
            handleSubmit('primary_color', '#0032FA');
        }
    }, [blocks]);

    const requestIfChanged = React.useCallback(
        async (place, updateBlockState) => {
            const { block: blockId } = place;

            // Find relevant blocks
            const foundBlock = findBlockById(blocks, blockId);
            if (!foundBlock) return null;

            const previousBlock = findBlockById(previousBlocks, blockId);
            const currentBlock = currentBlocks?.find(block => block?.id === blockId) ?? { data: '' };
            const widgetBlock = blocks.find(block => block.type === 'event-widget');

            // Return cached data if no changes
            if (previousBlock && JSON.stringify(foundBlock) === JSON.stringify(previousBlock)) {
                const result = createBlockData(place, foundBlock, currentBlock.data);
                setCurrentBlocks(prev => [...prev, result]);
                return;
            }

            const blockData = createBlockData(place, foundBlock);

            try {
                const response = await retry(
                    async bail => {
                        const result = await fetcher.get(
                            `/sites/organizations/${organization.id}/sites/${minisite.id}/blocks/${foundBlock.id}/preview${
                                widgetBlock?.id === place.block ? `?primary_color=${initialValues.primary_color.substring(1)}&language=${initialValues.language}` : ''
                            }`,
                            { cache: false }
                        );

                        if (result.status === 404) bail(new Error('Not found'));
                        return result;
                    },
                    { retries: 3, minTimeout: 1000 }
                );

                const result = response.status === 200 ? { ...blockData, data: response.response } : null;
                setCurrentBlocks(prev => [...prev, result]);
                return result;
            } catch (error) {
                console.error(`Error fetching block ${blockId}:`, error);
                setCurrentBlocks(prev => [...prev, createBlockData(place, foundBlock)]);
            }
        },
        [blocks, previousBlocks, currentBlocks, organization, minisite, initialValues]
    );

    React.useEffect(() => {
        if (!tab || !blocks || isLoadingMinisite || isLoadingBlocks) return;

        if (tab.template !== currentTemplate) {
            fetcher.get(`/sites/templates/${tab.template}`, { cache: false }).then(({ response }) => {
                setHtml(response['content']);
                setCurrentTemplate(tab.template);
            });
        }
        // We only create the requests if there's a change between the blocks and their previous value
        if (previousBlocks && JSON.stringify(previousBlocks) === JSON.stringify(blocks)) return;

        setCurrentBlocks([]); // Reset blocks before new requests
        tab.placements.forEach(requestIfChanged);
        setPreviousBlocks(blocks);
    }, [tab, blocks, previousBlocks, isLoadingMinisite, isLoadingBlocks, initialValues, requestIfChanged]);

    const EVENTS_BLOCKS_MAP = {
        'event-image-header': { related: 'image-header', param: 'url', create_event: true },
        'image-header': { related: 'event-image-header', param: 'event_banner', create_event: false },
        'event-social-opengraph': { related: 'seo', param: 'social_img', create_event: true },
        seo: { related: 'event-social-opengraph', param: 'event_social', create_event: false }
    };

    const updateOrCreateBlock = React.useCallback(
        (name, data) => {
            const currentBlock = getBlock(blocks, name);

            // Special Cases
            const mapValue = EVENTS_BLOCKS_MAP[name];
            if (mapValue) {
                if (getBlock(blocks, mapValue.related)) {
                    handleDelete(mapValue.param);
                }
                if (mapValue.create_event) {
                    if (!getBlock(blocks, name)) {
                        createBlockEventRelated(name);
                    }
                    return;
                }
            }

            if (currentBlock) {
                // We found a block, we have to update it
                mixpanel.track('ACTION - Minisite > Update Block', { block_name: name });
                const newBlock = new Block();
                newBlock.id = currentBlock.id;
                const cssData = name === 'perso-css' ? { ...blockCssPayload } : {};
                newBlock.payload = { ...cssData, ...data };
                newBlock
                    .save({ organizationId: organization.id, siteId: siteId })
                    .then(({ response }) => {
                        if (name === 'image-header' || name === 'event-image-header') {
                            const newMinisite = new Minisite();
                            newMinisite.id = siteId;
                            const placements = [{ block: response.id, name: BLOCK_PLACEMENT[name], position: 0 }];
                            newMinisite.tabs = [
                                {
                                    id: minisite.tabs[tabIndex].id,
                                    placements
                                }
                            ];
                            newMinisite.save({ organizationId: organization.id });
                        }
                        //refresh iframe
                        updateIframe();
                    })
                    .catch(e => {
                        console.error(e);
                    });
            } else {
                // No block found, we have to create a block first & link it to the minisite
                if (name !== 'perso-css') {
                    mixpanel.track('ACTION - Minisite > Create Block', { block_name: name });
                }
                const newMinisite = new Minisite();
                newMinisite.id = siteId;

                const block = new Block();
                block.type = name;
                block.payload = { ...data };
                block
                    .save({ organizationId: organization.id, siteId: siteId })
                    .then(({ response }) => {
                        const placements = [{ block: response.id, name: BLOCK_PLACEMENT[name], position: 0 }];
                        newMinisite.tabs = [
                            {
                                id: minisite.tabs[tabIndex].id,
                                placements
                            }
                        ];
                        if (name === 'image-header' || name === 'event-image-header') {
                            const tabPassword = minisite.tabs.find(tab => tab.slug === '_password');
                            if (tabPassword) {
                                const placementPassword = [{ block: response.id, name: BLOCK_PLACEMENT[name], position: 0 }];
                                newMinisite.tabs.push({
                                    id: tabPassword.id,
                                    placements: placementPassword
                                });
                            }
                        }
                        newMinisite
                            .save({ organizationId: organization.id })
                            .then(() => {
                                //refresh iframe
                                updateIframe();
                            })
                            .catch(e => {
                                console.error(e);
                            });
                    })
                    .catch(e => {
                        console.error(e);
                    });
            }
        },
        [organization, minisite, blockCssPayload, siteId, blocks]
    );

    const handleDelete = React.useCallback(
        name => {
            //We don't delete the block but only remove the block from site
            const currentBlock = getBlock(blocks, BLOCK_MAPPING[name]);
            const newMinisite = new Minisite();
            newMinisite.id = siteId;

            if (currentBlock) {
                const placements = minisite.tabs[tabIndex].placements;

                const newPlacements = placements
                    .filter(placement => placement.block === currentBlock.id)
                    .map(placement => {
                        return { ...placement, position: null };
                    });
                newMinisite.tabs = [
                    {
                        id: minisite.tabs[tabIndex].id,
                        placements: newPlacements
                    }
                ];
                const tabPassword = minisite.tabs.find(tab => tab.slug === '_password');
                if (tabPassword) {
                    const placementPassword = tabPassword.placements
                        .filter(placement => placement.block === currentBlock.id)
                        .map(placement => {
                            return { ...placement, position: null };
                        });
                    newMinisite.tabs.push({
                        id: tabPassword.id,
                        placements: placementPassword
                    });
                }
                newMinisite
                    .save({ organizationId: organization.id })
                    .then(() => {
                        //refresh iframe & sync
                        updateIframe();
                    })
                    .catch(e => {
                        console.error(e);
                    });
            }
        },
        [blocks, minisite]
    );

    const updateEventURL = React.useCallback(url => {
        let eventObject = new EventModel({
            id: event.id,
            gemino_url: `https://${minisite.domain}/${url}`
        });
        eventObject.save({ organizationId: organization.id });
    });

    // Calculate the last placement position
    const lastPlacementPosition = React.useMemo(() => {
        if (minisite?.tabs?.length === 0) {
            throw new Error('This minisite has no tabs.');
        }

        // Get and sort block positions in descending order from the first tab's placements in the minisite object
        // We take the first tab (tabs[0]) by default, as the second tab (tabs[1]) is used for site password protection
        const placements = minisite?.tabs[0]?.placements?.map(block => block.position).sort((a, b) => b - a);

        // If there are no placements or if the placements array is empty, the position is 0
        if (!placements?.length) return 0;

        return placements[0] + 1;
    }, [minisite]);

    const createBlockEventRelated = React.useCallback(
        (name, customSuccessMessage = null) => {
            // No block found, we have to create a block first & link it to the minisite
            const newMinisite = new Minisite();
            newMinisite.id = siteId;

            const block = new Block();
            block.type = name;
            block.payload = {};
            block.event = event.id;
            block
                .save({ organizationId: organization.id, siteId: siteId })
                .then(({ response }) => {
                    const placements = [{ block: response.id, name: BLOCK_PLACEMENT[name], position: lastPlacementPosition }];
                    newMinisite.tabs = [
                        {
                            id: minisite.tabs[tabIndex].id,
                            placements
                        }
                    ];
                    newMinisite
                        .save({ organizationId: organization.id })
                        .then(() => {
                            //refresh iframe
                            updateIframe();
                            toast.success(customSuccessMessage || t('ticket.event.minisite.block.createSuccess'));
                        })
                        .catch(e => {
                            console.error(e);
                        });
                })
                .catch(e => {
                    console.error(e);
                });
        },
        [organization, siteId, event, minisite, lastPlacementPosition]
    );

    const handlePosition = React.useCallback(
        (block, step) => {
            const position = block.position;
            const newPosition = position + step;

            const newMinisite = new Minisite();
            newMinisite.id = siteId;

            const placements = [{ block: block.id, name: block.place, position: newPosition }];
            newMinisite.tabs = [
                {
                    id: minisite.tabs[tabIndex].id,
                    placements
                }
            ];
            return newMinisite
                .save({ organizationId: organization.id })
                .then(() => {
                    minisite.is_live = false;
                    return null;
                })
                .catch(e => {
                    console.error(e);
                    return e;
                });
        },
        [updateIframe, minisite, organization, tabIndex]
    );

    const handleSubmit = React.useCallback(
        (name, value) => {
            setIsLoading(true);
            setSubmitSuccess(false);
            let data = {};
            switch (name) {
                case 'primary_color':
                    data = { primary_color: value };
                    if (blockCssPayload.light_primary_color === '#F5F5F5') {
                        data.light_primary_color = hexToRGBA(value, 0.1);
                    }
                    break;
                case 'social_img':
                    data = value;
                    break;
                case 'reset_colors':
                    data = value;
                    break;
                default:
                    data = { [NAME_ATTR[name] || name]: value };
            }
            if (name === 'password' || name === 'password_protection' || name === 'slug' || name === 'enable_seo_index' || name === 'language') {
                const newMinisite = new Minisite();
                newMinisite.id = siteId;
                if (name === 'password') {
                    newMinisite['password_protection'] = false;
                }
                newMinisite[name] = value;

                newMinisite
                    .save({ organizationId: organization.id })
                    .then(async () => {
                        toast.success(t('ticket.event.minisite.toast.saveSuccess'));
                        setSubmitSuccess(true);
                        if (name === 'slug') {
                            if (minisite.is_published) {
                                await publishVersion();
                            }
                            updateEventURL(value);
                        }
                        updateIframe();
                    })
                    .catch(e => {
                        console.error(e);
                        // If the slug already exists, we display a toast
                        if (e.response && e.response.slug && e.response.slug[0] === '{domain}/{slug} is already taken') {
                            toast.error(t('ticket.event.minisite.security_options.slug_already_exist'));
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            } else {
                updateOrCreateBlock(BLOCK_MAPPING[name], data);
            }
        },
        [organization, minisite, blockCssPayload, siteId, updateIframe]
    );

    const [showMenu, setShowMenu] = React.useState(true);
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const onCloseModal = React.useCallback(() => {
        setIsOpen(false);
        setEditBlock(null);
        setCurrentPosition(null);
        setSelectedPlace(null);
    }, []);

    const toggleMenu = React.useCallback(() => setShowMenu(!showMenu), [showMenu, setShowMenu]);

    const partialPublish = React.useCallback(() => {
        setIsSaving(true);
        return minisite
            .publish({
                organizationId: organization.pk
            })
            .then(() => {
                updateIframe();
                toast.success(t(`ticket.event.minisite.toast.publishSuccess`));
                setScheduleModal(false);
                mixpanel.track('ACTION - Minisite > Re-Publish');
            })
            .catch(e => {
                toast.error(t(`ticket.event.minisite.toast.publishError`));
            })
            .finally(() => setIsSaving(false));
    }, [minisite, organization, toast, mixpanel, updateIframe]);

    const publishVersion = React.useCallback(
        (type = 'now') => {
            if (type === 'event') {
                const newMinisite = new Minisite();
                newMinisite.id = siteId;
                newMinisite.delivery = { auto_on_publish: true, on_date: null };
                return newMinisite
                    .save({ organizationId: organization.id })
                    .then(() => {
                        updateIframe();
                        reloadEventContext(false);
                        toast.success(t(`ticket.event.minisite.toast.publishSuccess`));
                        mixpanel.track('ACTION - Minisite > Publish', {
                            type: 'follow-event'
                        });
                    })
                    .catch(e => {
                        toast.error(t(`ticket.event.minisite.toast.publishError`));
                    });
            } else {
                return minisite
                    .publish({
                        organizationId: organization.pk,
                        schedule: type === 'scheduled' ? schedule : null
                    })
                    .then(() => {
                        const newMinisite = new Minisite();
                        newMinisite.id = siteId;
                        newMinisite.delivery = { on_publish: [] };
                        return newMinisite
                            .save({ organizationId: organization.id })
                            .then(() => {
                                updateIframe();
                                reloadEventContext(false);
                                toast.success(t(`ticket.event.minisite.toast.${type === 'scheduled' ? 'scheduleSuccess' : 'publishSuccess'}`));
                                setScheduleModal(false);
                                mixpanel.track('ACTION - Minisite > Publish', {
                                    type: type === 'scheduled' ? 'scheduled' : 'now',
                                    minisite_publish_date: type === 'scheduled' ? schedule : null
                                });
                            })
                            .catch(e => {
                                toast.error(t(`ticket.event.minisite.toast.publishError`));
                            });
                    })
                    .catch(e => {
                        toast.error(t(`ticket.event.minisite.toast.publishError`));
                    });
            }
        },
        [minisite, schedule, organization, mixpanel, updateIframe, siteId, toast, schedule, reloadEventContext]
    );

    const isConverted = React.useMemo(() => {
        const created = new Date(event.created) || new Date();
        const launch = new Date('2021-10-05T00:00:00Z');
        // Events created after 2021-10-05 automaticaly trigger a mini-site creation
        // Only those created before 2021-10-05 must convert their mini-site
        return created - launch < 0;
    }, [event]);

    const unpublishVersion = React.useCallback(() => {
        minisite
            .unpublish({
                organizationId: organization.pk
            })
            .then(() => {
                updateIframe();
                reloadEventContext(false);
                toast.success(t(`ticket.event.minisite.toast.unpublishSuccess`));
                mixpanel.track('ACTION - Minisite > Unpublish');
            })
            .catch(e => {
                toast.error(t(`ticket.event.minisite.toast.unpublishError`));
            });
    }, [minisite, organization, mixpanel, updateIframe, toast, reloadEventContext]);

    const onSelectSchedule = React.useCallback(
        type => {
            switch (type) {
                case 'offline':
                    setSchedule(null);
                    unpublishVersion();
                    break;
                case 'scheduled':
                    setScheduleModal(true);
                    break;
                case 'event':
                    setSchedule(null);
                    publishVersion('event');
                    break;
                default:
                    setSchedule(null);
                    publishVersion();
                    break;
            }
        },
        [publishVersion, unpublishVersion]
    );

    const dropdownItems = React.useMemo(() => {
        let _dropdownItems = [
            {
                key: 2,
                label: t('ticket.event.minisite.header.preview'),
                onClick: openPreview,
                mobile: true
            }
        ];
        if (!standalone && !showMenu) {
            _dropdownItems.push({
                key: 0,
                label: t('ticket.event.minisite.header.personalize'),
                onClick: toggleMenu,
                mobile: true
            });
        }
        return _dropdownItems;
    }, [standalone, showMenu, toggleMenu, openPreview, organization, permissions]);

    if (!blocks || !minisite) {
        return <Loader />;
    }

    const disablePublish = isToday(schedule) && isBeforeHour(schedule) && isBeforeMinute(schedule);
    const closePublishModal = () => {
        setScheduleModal(false);
    };

    const renderEditMenu = (
        <EditMenu
            standalone={standalone}
            organization={organization}
            event={event}
            toggleMenu={toggleMenu}
            showMenu={showMenu}
            handleSubmit={handleSubmit}
            handleDelete={handleDelete}
            initialValues={initialValues}
            minisite={minisite}
            onSelectSchedule={onSelectSchedule}
            submitSuccess={submitSuccess}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            createBlockEventRelated={createBlockEventRelated}
            hasTranslationCapability={hasTranslationCapability}
        />
    );

    const innerContent = (
        <div className={Styles['minisite-container']}>
            <style>{`.wz-header, .wz-header-block { margin-bottom: 0; }`}</style>
            <WeezHeader
                title={
                    <span className={Styles['wz-header-title-custom']}>
                        <span className={Styles['title']}>{t('ticket.event.minisite.personalize')}</span>
                        {minisite.password_protection && <Status text={<Icon name="lock" margin={false} />} color="blue" />}
                    </span>
                }
                dropdownProps={{ renderWhenClosed: true }}
                dropdown={dropdownItems}
                rightComponent={[
                    <Button
                        primary
                        basic
                        key={'secondary'}
                        label={
                            <React.Fragment>
                                <Icon name={'eye'} /> <Trans i18nKey="ticket.event.minisite.header.preview" />
                            </React.Fragment>
                        }
                        onClick={openPreview}
                    />,
                    !standalone && !showMenu && (
                        <Button
                            primary
                            basic
                            key={'menu'}
                            inverted
                            onClick={toggleMenu}
                            label={
                                <React.Fragment>
                                    <Trans i18nKey="ticket.event.minisite.header.personalize" />
                                </React.Fragment>
                            }
                        />
                    ),
                    minisite.is_published && (
                        <Button
                            primary
                            disabled={minisite.is_live}
                            isLoading={isSaving}
                            key="primary"
                            label={<Trans i18nKey="ticket.common.action.save" />}
                            onClick={() => partialPublish()}
                        />
                    )
                ]}
            >
                <div className={Styles['header-content']}>
                    {minisite.domain && minisite.slug && (
                        <span className={Styles['link-content']}>
                            <LinkWithActions
                                disabled={!minisite.is_published}
                                tooltipText={
                                    minisite.is_published ? (
                                        <Trans i18nKey="ticket.event.minisite.header.tooltip" />
                                    ) : (
                                        <Trans i18nKey="ticket.event.minisite.header.tooltipDisabled" />
                                    )
                                }
                                url={`https://${minisite.domain}/${minisite.slug}`}
                                position={'bottom-start'}
                            />
                        </span>
                    )}
                    {!minisite.is_live && minisite.is_published && (
                        <span className={Styles['draft-content']}>
                            | <Icon name="message-warning" className={Styles['draft-icon']} /> {t('ticket.event.minisite.header.pending')}
                        </span>
                    )}
                </div>
                {minisite.is_spam && (
                    <Message type="danger" title={t('ticket.event.minisite.header.spam-title')}>
                        {/*The translation text includes a mailto link*/}
                        <div dangerouslySetInnerHTML={{ __html: t('ticket.event.minisite.header.spam-help-html') }} />
                    </Message>
                )}
            </WeezHeader>
            {!standalone && renderEditMenu}
            {html && (
                <Template
                    organization={organization}
                    event={event}
                    html={html}
                    selectPlace={onSelectPlace}
                    selectPosition={onSelectPosition}
                    blocks={currentBlocks}
                    onDelete={block => setDeleteBlock(block)}
                    onEdit={onEdit}
                    createBlock={createBlockEventRelated}
                    handlePosition={handlePosition}
                    handleSubmit={handleSubmit}
                />
            )}
            <Route exact path={'*/preview'}>
                <Modal
                    hideCloseButton
                    onClose={closePreview}
                    allowExternalClosing={false}
                    size="fullscreen"
                    className={`modal-fullscreen modal-preview-${iframeSize}`}
                    style={blockCss?.payload?.background_color ? { backgroundColor: blockCss?.payload?.background_color } : {}}
                >
                    <iframe
                        id="iframe"
                        title="Minisite preview"
                        style={{ ...VIEWPORT_SIZE[iframeSize], border: '0 solid transparent' }}
                        src={`${minisite.preview_url}${langParam ? `?lang=${langParam}` : ''}`}
                    ></iframe>
                    <div className="modal-actions">
                        <div className="modal-actions-items">
                            <div className="modal-actions-text">
                                <div>
                                    <Trans i18nKey="ticket.event.minisite.preview.content" />
                                </div>
                                <div className="modal-link-with-actions">
                                    <span className="modal-link-with-actions-text">
                                        <Trans i18nKey="ticket.event.minisite.preview.site" />
                                    </span>
                                    <span className={Styles['link-content']}>
                                        <LinkWithActions
                                            disabled={!minisite.is_published}
                                            tooltipText={
                                                minisite.is_published ? (
                                                    <Trans i18nKey="ticket.event.minisite.header.tooltip" />
                                                ) : (
                                                    <Trans i18nKey="ticket.event.minisite.header.tooltipDisabled" />
                                                )
                                            }
                                            url={`https://${minisite.domain}/${minisite.slug}`}
                                        />
                                    </span>
                                </div>
                            </div>
                            <div className="modal-actions-left">
                                <div className="modal-actions-icons">
                                    <Icon
                                        name="mobile-alt"
                                        size="huge"
                                        className={`modal-actions-icon${iframeSize === 'mobile' ? ' active' : ''}`}
                                        onClick={() => setIframeSize('mobile')}
                                    />
                                    <Icon
                                        name="tablet"
                                        size="huge"
                                        className={`modal-actions-icon${iframeSize === 'tablet' ? ' active' : ''}`}
                                        onClick={() => setIframeSize('tablet')}
                                    />
                                    <Icon
                                        name="desktop"
                                        size="huge"
                                        className={`modal-actions-icon${iframeSize === 'desktop' ? ' active' : ''}`}
                                        onClick={() => setIframeSize('desktop')}
                                    />
                                </div>
                                <Button primary label={t(`ticket.event.minisite.preview.back`)} onClick={closePreview} />
                            </div>
                        </div>
                    </div>
                </Modal>
            </Route>
            {selectedPlace && (
                <Modal open={isOpen} onClose={onCloseModal} allowExternalClosing={false} size="large">
                    <BlockFormContainer
                        organization={organization}
                        minisite={minisite}
                        eventLanguage={event.default_language}
                        place={selectedPlace}
                        blocks={blocks}
                        tabIndex={tabIndex}
                        currentBlock={editBlock}
                        currentPosition={currentPosition}
                        onCloseModal={onCloseModal}
                        additionnalData={initialValues}
                        onSuccess={() => {
                            setIsOpen(false);
                            syncMinisite();
                            syncBlocks();
                            setEditBlock(null);
                        }}
                        hasTranslationCapability={hasTranslationCapability}
                    />
                </Modal>
            )}
            <ConfirmModal
                allowExternalClosing
                labelButton={t('ticket.common.action.save')}
                labelTitle={t('ticket.event.minisite.publishModal.title')}
                labelBody={
                    <div className="margin-scheduler">
                        <Form.Field>
                            <SchedulePublish schedule={schedule} setSchedule={setSchedule} disablePublish={disablePublish} />
                        </Form.Field>
                    </div>
                }
                objectToSave={Boolean(scheduleModal) && !minisite.is_published}
                onClose={() => closePublishModal()}
                onAccept={() => publishVersion('scheduled')}
                disabled={schedule === null || disablePublish}
            />
            {deleteBlock && (
                <ConfirmModal
                    labelButton={t('ticket.common.action.delete')}
                    labelTitle={t('ticket.event.minisite.deleteModal.title')}
                    labelBody={<Trans i18nKey="ticket.event.minisite.deleteModal.content" />}
                    objectToSave={minisite}
                    onClose={() => setDeleteBlock(false)}
                    onAccept={() => onDeleteBlock()}
                />
            )}
            {giveFeedback && (
                <Modal
                    size="large"
                    allowExternalClosing={false}
                    open={Boolean(giveFeedback)}
                    onClose={() => setGiveFeedback(false)}
                    title={t('ticket.event.minisite.feedbackModal.title')}
                    submitButton={<Button primary onClick={() => onGiveFeedback()} label={t('ticket.common.action.send')} disabled={feedbackForm === ''} />}
                    cancelButton={<Button onClick={() => setGiveFeedback(false)} label={t(`ticket.common.action.cancel`)} />}
                >
                    <Trans i18nKey="ticket.event.minisite.feedbackModal.content" />
                    <br />
                    <br />
                    <Form.TextArea
                        showSizes
                        maxlength={300}
                        placeholder={t('ticket.form.modal.customFields.descriptionPlaceholder')}
                        onChange={({ target: { value } }) => {
                            setFeedbackForm(value);
                        }}
                        value={feedbackForm}
                    />
                    <br />
                    <br />
                    <Trans i18nKey="ticket.event.minisite.feedbackModal.subtitle" />
                </Modal>
            )}
            {permissions.capacities.has('PyvarCapacity.MINISITE_ONBOARDING') && (
                <WelcomeModal
                    title={
                        <div
                            style={{
                                width: '100%',
                                margin: 0,
                                textAlign: 'center'
                            }}
                        >
                            <span role="img" aria-label="tada emoji" style={{ marginRight: '10px' }}>
                                🎉
                            </span>
                            <Trans i18nKey="ticket.event.minisite.welcomeModal.title" />
                            <span role="img" aria-label="tada emoji" style={{ marginLeft: '5px' }}>
                                🎉
                            </span>
                        </div>
                    }
                    labelCTA={<Trans i18nKey="ticket.event.minisite.welcomeModal.cta" />}
                    storageValue="minisite_onboarding"
                >
                    <img src="https://gallery.weezevent.com/229059/sites/edition-minisite.png" alt="onboarding minisite" style={{ width: '100%', marginBottom: '10px' }} />
                    <div>
                        <Trans i18nKey="ticket.event.minisite.welcomeModal.content" />
                        {isConverted && (
                            <>
                                <br />
                                <br />
                                <Trans i18nKey="ticket.event.minisite.welcomeModal.more-content" />
                            </>
                        )}
                    </div>
                </WelcomeModal>
            )}
        </div>
    );

    if (standalone) {
        return <MenuContainer menu={renderEditMenu}>{innerContent}</MenuContainer>;
    } else {
        return innerContent;
    }
}

MinisiteEditContainer.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired
};
